import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowLeft, RefreshCw, CheckCircle, XCircle, Search, Globe, Pencil, Check, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';

import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
    return { Authorization: `Bearer ${getToken()}` };
}

// Team badge using PA API
function TeamBadge({ teamId, size = 28 }) {
    if (!teamId) return null;
    const numId = teamId.startsWith('t') ? teamId.slice(1) : teamId;
    return (
        <img
            src={`https://secure.cache.images.core.optasports.com/soccer/teams/150x150/${numId}.png`}
            alt=""
            width={size}
            height={size}
            className="rounded-sm object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
        />
    );
}

// Confederation badge colour
function ConfBadge({ conf }) {
    if (!conf) return null;
    const colors = {
        UEFA:     'bg-blue-500/20 text-blue-300',
        CONMEBOL: 'bg-yellow-500/20 text-yellow-300',
        CONCACAF: 'bg-red-500/20 text-red-300',
        CAF:      'bg-green-500/20 text-green-300',
        AFC:      'bg-purple-500/20 text-purple-300',
        OFC:      'bg-teal-500/20 text-teal-300',
    };
    const cls = colors[conf] || 'bg-muted text-muted-foreground';
    return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cls}`}>{conf}</span>;
}

function MethodBadge({ method }) {
    if (!method) return null;
    if (method === 'manual') return <span className="text-[10px] text-amber-400 font-medium">manual</span>;
    if (method.startsWith('code:')) return <span className="text-[10px] text-emerald-400 font-medium">auto (code)</span>;
    if (method.startsWith('fuzzy:')) {
        const score = method.split(':')[1];
        return <span className="text-[10px] text-orange-400 font-medium">fuzzy ({score})</span>;
    }
    return <span className="text-[10px] text-muted-foreground">{method}</span>;
}

export default function AdminWCMappingsPage() {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchPA, setSearchPA] = useState('');

    // Edit dialog state
    const [editTeam, setEditTeam] = useState(null);     // PA team being edited
    const [fifaTeams, setFifaTeams] = useState([]);      // all 211 FIFA teams
    const [loadingFifa, setLoadingFifa] = useState(false);
    const [fifaSearch, setFifaSearch] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchMappings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/admin/worldcup/fifa-mappings`, { headers: authHeaders() });
            setTeams(res.data.teams || []);
        } catch {
            toast.error('Failed to load mappings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMappings(); }, [fetchMappings]);

    async function openEdit(team) {
        setEditTeam(team);
        setFifaSearch('');
        if (fifaTeams.length === 0) {
            setLoadingFifa(true);
            try {
                const res = await axios.get(`${API_URL}/api/admin/worldcup/fifa-all-teams`, { headers: authHeaders() });
                setFifaTeams(res.data.teams || []);
            } catch {
                toast.error('Failed to load FIFA teams');
            } finally {
                setLoadingFifa(false);
            }
        }
    }

    async function saveMapping(fifaTeamId, fifaTeamName) {
        setSaving(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/worldcup/fifa-mappings/override`,
                { pa_team_id: editTeam.pa_team_id, fifa_team_id: fifaTeamId },
                { headers: authHeaders() }
            );
            toast.success(`Mapped ${editTeam.pa_team_name} → ${res.data.fifa_team} (Rank #${res.data.rank})`);
            setEditTeam(null);
            fetchMappings();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save mapping');
        } finally {
            setSaving(false);
        }
    }

    const filtered = teams.filter(t =>
        t.pa_team_name.toLowerCase().includes(searchPA.toLowerCase()) ||
        (t.fifa_team_name || '').toLowerCase().includes(searchPA.toLowerCase())
    );
    const mapped = filtered.filter(t => t.mapped);
    const unmapped = filtered.filter(t => !t.mapped);

    const filteredFifa = fifaTeams.filter(f =>
        f.fifa_team_name.toLowerCase().includes(fifaSearch.toLowerCase()) ||
        f.country_code.toLowerCase().includes(fifaSearch.toLowerCase())
    );

    return (
        <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Link to="/admin/football">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                </Link>
                <div>
                    <h1 className="text-2xl font-bold page-title">WC Team Mappings</h1>
                    <p className="text-sm text-muted-foreground">
                        Review and fix FIFA ↔ PA team matches
                    </p>
                </div>
                <Button variant="outline" size="sm" className="ml-auto gap-2" onClick={fetchMappings} disabled={loading}>
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Summary */}
            {!loading && (
                <div className="flex gap-3">
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className="text-2xl font-bold text-emerald-400">{teams.filter(t => t.mapped).length}</p>
                        <p className="text-xs text-muted-foreground">Mapped</p>
                    </div>
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className="text-2xl font-bold text-red-400">{teams.filter(t => !t.mapped).length}</p>
                        <p className="text-xs text-muted-foreground">Unmapped</p>
                    </div>
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className="text-2xl font-bold">{teams.length}</p>
                        <p className="text-xs text-muted-foreground">Total Teams</p>
                    </div>
                </div>
            )}

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Search teams..."
                    value={searchPA}
                    onChange={e => setSearchPA(e.target.value)}
                    className="pl-9"
                />
            </div>

            {loading ? (
                <div className="space-y-2">
                    {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Unmapped first — needs attention */}
                    {unmapped.length > 0 && (
                        <div className="space-y-2">
                            <h2 className="text-sm font-semibold text-red-400 flex items-center gap-1.5">
                                <XCircle className="h-4 w-4" />
                                Unmapped ({unmapped.length})
                            </h2>
                            {unmapped.map(team => (
                                <TeamRow key={team.pa_team_id} team={team} onEdit={openEdit} />
                            ))}
                        </div>
                    )}

                    {/* Mapped */}
                    {mapped.length > 0 && (
                        <div className="space-y-2">
                            <h2 className="text-sm font-semibold text-emerald-400 flex items-center gap-1.5">
                                <CheckCircle className="h-4 w-4" />
                                Mapped ({mapped.length})
                            </h2>
                            {mapped.map(team => (
                                <TeamRow key={team.pa_team_id} team={team} onEdit={openEdit} />
                            ))}
                        </div>
                    )}

                    {filtered.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                            <Globe className="h-8 w-8 mx-auto mb-3 opacity-30" />
                            <p>No teams found. Run &quot;Sync FIFA Team Intel&quot; first.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Edit Dialog */}
            <Dialog open={!!editTeam} onOpenChange={open => !open && setEditTeam(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TeamBadge teamId={editTeam?.pa_team_id} size={24} />
                            Assign FIFA team for {editTeam?.pa_team_name}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search FIFA teams..."
                                value={fifaSearch}
                                onChange={e => setFifaSearch(e.target.value)}
                                className="pl-9"
                                autoFocus
                            />
                        </div>

                        <div className="max-h-72 overflow-y-auto space-y-1 rounded-md border p-1">
                            {loadingFifa ? (
                                <div className="py-6 text-center text-muted-foreground text-sm">
                                    <RefreshCw className="h-4 w-4 animate-spin mx-auto mb-2" />
                                    Loading FIFA teams...
                                </div>
                            ) : filteredFifa.length === 0 ? (
                                <p className="py-4 text-center text-muted-foreground text-sm">No teams match</p>
                            ) : (
                                filteredFifa.map(f => (
                                    <button
                                        key={f.fifa_team_id}
                                        onClick={() => saveMapping(f.fifa_team_id, f.fifa_team_name)}
                                        disabled={saving}
                                        className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent text-sm text-left transition-colors disabled:opacity-50"
                                    >
                                        <span className="w-6 text-center text-xs text-muted-foreground font-mono">
                                            #{f.rank}
                                        </span>
                                        <span className="flex-1 font-medium">{f.fifa_team_name}</span>
                                        <span className="text-xs text-muted-foreground font-mono">{f.country_code}</span>
                                        <ConfBadge conf={f.confederation} />
                                        {editTeam?.fifa_team_id === f.fifa_team_id && (
                                            <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditTeam(null)} disabled={saving}>
                            <X className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function TeamRow({ team, onEdit }) {
    return (
        <div className={`flex items-center gap-3 p-3 rounded-lg border transition-colors
            ${team.mapped ? 'bg-card border-border' : 'bg-red-950/20 border-red-800/30'}`}>
            {/* PA team */}
            <TeamBadge teamId={team.pa_team_id} size={32} />
            <div className="w-32 shrink-0">
                <p className="text-sm font-medium leading-tight">{team.pa_team_name}</p>
                <p className="text-[10px] text-muted-foreground font-mono">{team.pa_team_id}</p>
            </div>

            {/* Arrow */}
            <span className="text-muted-foreground text-sm mx-1">→</span>

            {/* FIFA team */}
            {team.mapped ? (
                <div className="flex-1 flex items-center gap-2 min-w-0">
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-sm font-medium truncate">{team.fifa_team_name}</p>
                            {team.country_code && (
                                <span className="text-[10px] font-mono text-muted-foreground">{team.country_code}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                            {team.rank && (
                                <span className="text-[10px] text-muted-foreground">Rank #{team.rank}</span>
                            )}
                            <ConfBadge conf={team.confederation} />
                            <MethodBadge method={team.match_method} />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex-1">
                    <p className="text-sm text-red-400">Not mapped</p>
                    <p className="text-[10px] text-muted-foreground">Click Edit to assign a FIFA team</p>
                </div>
            )}

            {/* Edit button */}
            <Button
                variant={team.mapped ? 'ghost' : 'outline'}
                size="sm"
                className={`shrink-0 gap-1 ${!team.mapped ? 'border-red-500/40 text-red-400 hover:bg-red-950/30' : ''}`}
                onClick={() => onEdit(team)}
            >
                <Pencil className="h-3.5 w-3.5" />
                {team.mapped ? 'Change' : 'Assign'}
            </Button>
        </div>
    );
}
